import { Edit } from '@mui/icons-material';
import { Box, Button, Divider } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useEmployee } from '~/data/employee';
import { usePersonProfile } from '~/data/profile/usePersonProfile';
import { Roles } from '~/data/user';
import { SocialLinkEditDialog } from '~/features/EmployeeSocialLinks/components/SocialLinkEditDialog';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useDialogState } from '~/hooks/useDialogState';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { SocialLinkButton } from './components/SocialLinkButton';
import { useSxStyles } from './useStyles';

export type SocialLinksForm = {
  socialProfile: string;
  empPresentation: string;
};

export const EmployeeSocialLinks = () => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const editDialog = useDialogState();

  const {
    selectedEmployeeId,
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const canEdit =
    hasRoleAccessOverEmployee([Roles.MANAGER, Roles.HR_ADMIN]) ||
    isSelectedEmployeeLoggedInUser;

  const { employee, isLoading: isLoadingEmployee } = useEmployee();

  const { profileId, isProfileIdInSync } = useMemo(() => {
    const isEmployeeInSync =
      selectedEmployeeId === employee?.empBasicData?.fEmpId;

    const profileId = isEmployeeInSync
      ? employee.empBasicData.fEmpProfileId
      : undefined;

    return {
      isProfileIdInSync: isEmployeeInSync,
      profileId,
    };
  }, [employee, selectedEmployeeId]);

  const {
    data: socialLinkProfile,
    mutate,
    isLoading,
  } = usePersonProfile(
    {
      profileId,
    },
    { revalidateOnFocus: false },
    !isProfileIdInSync || isLoadingEmployee,
  );

  if (isLoading || isLoadingEmployee) {
    return (
      <LoadingView sx={sxStyles.loading} CircularProgressProps={{ size: 20 }} />
    );
  }

  return (
    <>
      <Box sx={sxStyles.root}>
        {socialLinkProfile?.socialProfile && (
          <SocialLinkButton url={socialLinkProfile.socialProfile} />
        )}
        {socialLinkProfile?.empPresentation && (
          <SocialLinkButton url={socialLinkProfile.empPresentation} />
        )}
        {canEdit && socialLinkProfile && (
          <>
            {(socialLinkProfile?.socialProfile ||
              socialLinkProfile?.empPresentation) && (
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={sxStyles.editDivider}
              />
            )}

            <Button
              onClick={editDialog.open}
              startIcon={<Edit fontSize={'small'} />}
              size="small"
            >
              {t('laEditSocialLinks')}
            </Button>
          </>
        )}
      </Box>
      {canEdit && socialLinkProfile && editDialog.isOpen && (
        <SocialLinkEditDialog
          personProfile={socialLinkProfile}
          open={editDialog.isOpen}
          onClose={editDialog.close}
          onUpdate={() => {
            mutate();
          }}
        />
      )}
    </>
  );
};
